// Cio che e DEL SETTE E MEZZO nel tavolo: il dominio delle rotte del BFF, i posti, come si compone l'azione, come si disegna una carta, le frasi dei rifiuti. Il trasporto (socket,
// ticket, REST, seed, hook) e `lib/gamehost/`: qui se ne passano i PARAMETRI e basta (ADR-0235 §4).
import type { PostoPubblico } from '@/lib/gamehost/protocol';
import { creaTavoloBody } from '@/lib/gamehost/rest';
import type { VociRifiuto } from '@/lib/gamehost/rifiuti';
import { facciaNapoletana } from '@/lib/tavolo/carta';
import type { FacciaNapoletana } from '@/lib/tavolo/carta';
import type { Carta, Mossa, MossaDiPersona } from './protocol';
import { MOTIVI } from './protocol';
import type { LivelloRobot } from './robot';

/** Il segmento di `/v1/games/<dominio>` (`limulo-app/gioco.js` di QSetteMezzo: `dominio: 'settemezzo'`). */
export const SETTEMEZZO_DOMINIO = 'settemezzo';

/**
 * I posti che il gioco ammette nel game host (`POSTI_AMMESSI` di `apps/qsettemezzo-gameserver/src/regole.ts`: da 2 a 6) e quello che la TMA apre: uno contro uno (regolamento §7.1, r. 4;
 * ranked e tornei non entrano: nascono in amichevole, contro il robot o su invito).
 */
export const POSTI_AMMESSI: readonly number[] = [2, 3, 4, 5, 6];
export const POSTI_SETTEMEZZO = 2;

// COPIA DICHIARATA da src/lib/ginrummy/gioco.ts @ 8a3aab5bf3a9cf073711ceeb9d50f7837089efaa (TMA#763): `RichiestaTavoloGin` e `creaTavoloGinRummyBody`, righe 17-33, che sono a loro volta la copia di TMA#746
// (briscola-orcb-8-tma) @ e823c650454b6adf11046bffc675774d6429937d, src/lib/briscola/gioco.ts, righe 39-47 (`creaTavoloBriscolaBody`). Cosa e diverso dall'originale: i nomi e i posti fissi a 2 (`POSTI_SETTEMEZZO`).
/**
 * La richiesta di un tavolo nuovo. Il livello dei robot esiste SOLO per un tavolo casual: per un privato («Invita un amico») il tipo lo vieta (`livello?: never`), e se arriva lo
 * stesso `creaTavoloSettemezzoBody` non lo porta.
 */
export type RichiestaTavolo = { tipo: 'casual'; idempotencyKey: string; livello?: LivelloRobot } | { tipo: 'privato'; idempotencyKey: string; livello?: never };

/**
 * Il corpo di un tavolo nuovo: i campi di sempre (`tipo`, `posti`, `idempotencyKey`) e, SOLO se casual e con un livello scelto, `robot: { livello }`. Un tavolo coi robot e SEMPRE casual
 * (un invito non ha senso: nessun posto da riempire) e non e mai classificato: nessun campo `classificato`. Forma di `rotte.js` del gioco (`tavolo()`: `robot` e `{ livello }` e nient'altro).
 */
export function creaTavoloSettemezzoBody(a: RichiestaTavolo) {
  const base = creaTavoloBody({ tipo: a.tipo, posti: POSTI_SETTEMEZZO, idempotencyKey: a.idempotencyKey });
  return a.tipo === 'casual' && a.livello !== undefined ? { ...base, robot: { livello: a.livello } } : base;
}

/**
 * L'azione che parte sul filo: la mossa scelta con il posto aggiunto, e NIENT'ALTRO. Il server la confronta con le `mosse(vista)` del posto (`puòAgire`) dopo aver controllato che le chiavi siano
 * ESATTAMENTE `n,posto,t` (puntate) o `posto,t` (carta, sto): una chiave in piu, o in meno, e `AZIONE_MALFORMATA`. Funzione di MODULO: vale prima e dopo che `useTavoloGamehost` tenga l'ultima
 * callback in un ref.
 */
export const componiAzione = (posto: number, da: MossaDiPersona): Mossa => ({ ...da, posto }) as Mossa;

/** La mossa del server (`mosse(vista)`) come la sceglie chi gioca, cioe senza il posto. */
export function senzaPosto(m: Mossa): MossaDiPersona {
  switch (m.t) {
    case 'PUNTA_CARTA':
    case 'PUNTA_STO':
      return { t: m.t, n: m.n };
    default:
      return { t: m.t };
  }
}

/** L'emoji del robot in testa all'etichetta, con l'eventuale selettore di presentazione e gli spazi che la seguono. */
const EMOJI_ROBOT_INIZIALE = /^\u{1F916}\uFE0F?\s*/u;

/**
 * Il nome di un posto: il nickname di chi siede; un posto libero (o senza nome) e `libero`, la frase che passa chi chiama. Mai un ripiego su email o nome anagrafico (ADR-0238).
 * Per un posto ROBOT il «🤖 » iniziale dell'etichetta (`ETICHETTA_BOT` del kit) non c'e: accanto al nome la sezione disegna sempre `<BadgeBot />` (ADR-0212), e le due icone sarebbero un doppione
 * (decisione di kit-giochi-orcr, 22/10). Il taglio e QUI, e non nei chiamanti, perche ogni punto che mostra il nome passa da questa funzione e ha il suo badge. Vale solo per `tipo: 'robot'`:
 * il nickname di una persona che comincia con «🤖» resta com'e.
 */
export function nomeDelPosto(posti: readonly PostoPubblico[], p: number, libero: string): string {
  const pp = posti.find((x) => x.posto === p);
  if (!pp || pp.tipo === 'libero') return libero;
  const nome = pp.tipo === 'robot' ? pp.etichetta.replace(EMOJI_ROBOT_INIZIALE, '') : pp.etichetta;
  return nome.trim() !== '' ? nome : libero;
}

/** La faccia del modulo tavolo per una carta (0..39): la STESSA Carta napoletana della scopa (`lib/tavolo/carta.ts`), dall'indice di QScopa. Fuori intervallo, `null` (non si disegna). */
export const facciaCarta = (c: Carta): FacciaNapoletana | null => facciaNapoletana(c);

/**
 * Le frasi dei rifiuti (`lib/gamehost/rifiuti.ts`). Il game host manda un `reason` per due sole mosse sbagliate (`MOTIVI`: PUNTATA_FUORI_LIMITE, TALLONE_FINITO); per tutte le altre decide il
 * `code` di `puòAgire` (FINITA, NON_DISTRIBUITO, NON_TUO, DA_PAGARE, ATTESA_SERVER, FUORI_TURNO, USCENTE, ILLEGALE) o del core (STALE_SEQ, THROTTLED). Un codice che non e qui ha la frase generica.
 */
export const VOCI_RIFIUTO: VociRifiuto = {
  motivi: MOTIVI,
  codici: ['FUORI_TURNO', 'NON_TUO', 'DA_PAGARE', 'ATTESA_SERVER', 'USCENTE', 'ILLEGALE', 'STALE_SEQ', 'FINITA', 'NON_DISTRIBUITO', 'THROTTLED'],
};
